import { useCallback } from 'react';
import useHistoryWithToken from '../hooks/useHistory';
import { useDispatch } from 'react-redux';
import { appSlice, initWorker, useSelectorQueryApp } from '../reducers/appReducer';
import { MODAL_PRACTICE } from '../components/shared/Modals/constants';
import { sectionsSlice } from '../reducers/sectionReducer';

/**
 *
 * @returns {{handleModal: ((function(*, *=, *=): void)|*), splashScreenPrintedAction: ((function(): void)|*), initStep: (function(*=): *), initTypeOfApp: (function(*=): *), setTestTechnique: ((function(*=): void)|*), refreshStep: ((function(*=): void)|*), closeAllModal: ((function(): void)|*), goToNextStep: ((function(): void)|*), sendConsents: ((function(*=): void)|*), closeAllToast: ((function(): void)|*), startPractice: ((function(*=): void)|*), i18nInitialized: ((function(*=): void)|*), errorDeviceAction: ((function(*=): void)|*), updatePractice: ((function(*): void)|*), handleToast: ((function(*, *, *=): void)|*), skipPractice: ((function(): void)|*), nextActionPractice: ((function(): (*))|*)}}
 */
export const useAppActions = () => {
	const { push } = useHistoryWithToken();
	const dispatch = useDispatch();
	const workflow = useSelectorQueryApp('workflow');
	const step = useSelectorQueryApp('step');
	const practice = useSelectorQueryApp('practice');

	const { MAJ_RTC } = sectionsSlice.actions;
	const {
		INIT_APP,
		INIT_STEPS,
		SPLASH_SCREEN_PRINTED,
		SEND_CONSENTS,
		REFRESH_STEP,
		HANDLE_MODAL,
		DEVICES_AUTH,
		MAJ_PRACTICE,
		CLOSE_ALL_MODAL,
		CLOSE_ALL_TOAST,
		HANDLE_TOAST,
		SET_TECHNIQUE_OK,
		I18N_INITIALIZED,
	} = appSlice.actions;
	const initTypeOfApp = useCallback((payload) => dispatch(INIT_APP(payload)), [INIT_APP, dispatch]);

	const i18nInitialized = useCallback(
		(payload) => {
			dispatch(I18N_INITIALIZED(payload));
		},
		[dispatch, I18N_INITIALIZED]
	);

	const initStep = useCallback((tpl) => dispatch(INIT_STEPS(tpl)), [INIT_STEPS, dispatch]);

	const setTestTechnique = useCallback(
		(payload = false) => {
			dispatch(SET_TECHNIQUE_OK(payload));
		},
		[dispatch, SET_TECHNIQUE_OK]
	);

	const closeAllModal = useCallback(() => {
		dispatch(CLOSE_ALL_MODAL());
	}, [dispatch, CLOSE_ALL_MODAL]);

	const closeAllToast = useCallback(() => {
		dispatch(CLOSE_ALL_TOAST());
	}, [dispatch, CLOSE_ALL_TOAST]);

	const refreshStep = useCallback(
		(payload) => {
			dispatch(REFRESH_STEP(payload));
		},
		[REFRESH_STEP, dispatch]
	);

	// issue with framer motion
	// https://stackoverflow.com/questions/63649192/how-to-prevent-re-render-of-parent-route-using-react-router-and-framer-motion
	const goToNextStep = useCallback(() => {
		const actualStepIndex = workflow.findIndex((elem) => elem.type === step.type);
		const data = workflow[actualStepIndex + 1];

		if (data) {
			push(data.route);
		}
	}, [step, workflow, push]);

	const sendConsents = useCallback(
		(consents, smartranking = false) => {
			dispatch(SEND_CONSENTS({ ...consents, smartranking }));
		},
		[SEND_CONSENTS, dispatch]
	);

	const splashScreenPrintedAction = useCallback(() => {
		dispatch(SPLASH_SCREEN_PRINTED());
	}, [SPLASH_SCREEN_PRINTED, dispatch]);

	/**
	 * @property {'show' | 'hide'} action
	 * @type {(function(*, *=, *=): void)|*}
	 */
	const handleModal = useCallback(
		(id, action = 'show', options = {}) => {
			dispatch(HANDLE_MODAL({ id, action, options }));
		},
		[dispatch, HANDLE_MODAL]
	);
	const handleToast = useCallback(
		(title, message, action = 'add') => {
			dispatch(HANDLE_TOAST({ title, message, action }));
		},
		[dispatch, HANDLE_TOAST]
	);

	const nextActionPractice = useCallback(() => {
		const actualModal = practice.modal + 1;

		if (actualModal === 1 || actualModal === 3 || actualModal === 4) {
			return dispatch(MAJ_PRACTICE({ modal: actualModal }));
		}
		if (actualModal === 5) {
			handleModal(MODAL_PRACTICE, 'hide');
			dispatch(MAJ_RTC({ startRecordAction: true, chronoReadIsStarted: true }));
			return dispatch(MAJ_PRACTICE({ needPractice: false }));
		}
		handleModal(MODAL_PRACTICE, 'hide');
		return setTimeout(() => {
			dispatch(MAJ_PRACTICE({ modal: actualModal }));
			dispatch(MAJ_RTC({ chronoReadIsStarted: true }));
		}, 300);
	}, [practice.modal, handleModal, MAJ_PRACTICE, dispatch, MAJ_RTC]);

	const skipPractice = useCallback(() => {
		handleModal(MODAL_PRACTICE, 'hide');
		dispatch(MAJ_PRACTICE({ skip: true, needPractice: false }));
		dispatch(MAJ_RTC({ chronoReadIsStarted: true }));
	}, [handleModal, dispatch, MAJ_PRACTICE, MAJ_RTC]);

	const updatePractice = useCallback(
		(payload) => {
			dispatch(MAJ_PRACTICE({ ...payload }));
		},
		[dispatch, MAJ_PRACTICE]
	);

	const startPractice = useCallback(
		(numberModal = 0) => {
			dispatch(
				MAJ_PRACTICE({
					skip: false,
					modal: numberModal,
					needPractice: true,
				})
			);
			dispatch(
				MAJ_RTC({
					chronoReadIsStarted: false,
				})
			);
			handleModal(MODAL_PRACTICE, 'show');
		},
		[dispatch, MAJ_PRACTICE, MAJ_RTC, handleModal]
	);

	const errorDeviceAction = useCallback(
		(payload = false) => {
			dispatch(DEVICES_AUTH(payload));
		},
		[dispatch, DEVICES_AUTH]
	);

	return {
		nextActionPractice,
		skipPractice,
		i18nInitialized,
		closeAllToast,
		updatePractice,
		initTypeOfApp,
		splashScreenPrintedAction,
		startPractice,
		closeAllModal,
		setTestTechnique,
		errorDeviceAction,
		initStep,
		goToNextStep,
		refreshStep,
		sendConsents,
		handleModal,
		handleToast,
	};
};

export default useAppActions;
